import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import {
  Database,
  Clock,
  ChevronLeft,
  ChevronRight,
  Globe,
  Zap,
  Unplug,
  RefreshCw,
  Activity,
  Play,
  Pause,
  AlertTriangle,
  Plus,
  Loader2,
  Filter,
  CheckSquare,
  Square,
} from 'lucide-react';
import type { StoreListItem, IngestionConfig } from './types';
import { STATUS_CONFIG, cronToHuman } from './types';
import { BackfillForm } from './BackfillForm';
import { InfoBadge } from '../../components/ui/Tooltip';

import shopifyLogo from '../../assets/platforms/shopify.svg';
import metaLogo from '../../assets/platforms/meta.svg';
import googleAdsLogo from '../../assets/platforms/google-ads.svg';
import tiktokLogo from '../../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../../assets/platforms/pinterest.svg';
import klaviyoLogo from '../../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../../assets/platforms/amazon-seller.svg';
import ga4Logo from '../../assets/platforms/ga4.svg';

// ─── Platform metadata ──────────────────────────────────

const PLATFORM_META: Record<string, { label: string; icon: string; category: 'ads' | 'commerce' | 'analytics' | 'email' }> = {
  shopify: { label: 'Shopify', icon: shopifyLogo, category: 'commerce' },
  meta: { label: 'Meta Ads', icon: metaLogo, category: 'ads' },
  google_ads: { label: 'Google Ads', icon: googleAdsLogo, category: 'ads' },
  tiktok_business: { label: 'TikTok Business', icon: tiktokLogo, category: 'ads' },
  tiktok_shop: { label: 'TikTok Shop', icon: tiktokShopLogo, category: 'commerce' },
  pinterest: { label: 'Pinterest', icon: pinterestLogo, category: 'ads' },
  klaviyo: { label: 'Klaviyo', icon: klaviyoLogo, category: 'email' },
  amazon_ads: { label: 'Amazon Ads', icon: amazonAdsLogo, category: 'ads' },
  amazon_seller: { label: 'Amazon Seller', icon: amazonSellerLogo, category: 'commerce' },
  ga4: { label: 'Google Analytics', icon: ga4Logo, category: 'analytics' },
};

const CATEGORY_LABELS: Record<string, string> = {
  ads: 'Ad Platforms',
  commerce: 'Commerce',
  analytics: 'Analytics',
  email: 'Email & Engagement',
};

const CATEGORY_ORDER = ['ads', 'commerce', 'analytics', 'email'] as const;

const STATUS_ORDER: Record<string, number> = {
  backfilling: 0,
  active: 1,
  paused: 2,
  disabled: 3,
};

const FILTER_OPTIONS = ['All', 'Active', 'Backfilling', 'Paused', 'Stale'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

// ─── Helpers ────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function isStale(cfg: IngestionConfig): boolean {
  if (cfg.status !== 'active') return false;
  const last = cfg.lastStoreActiveAt ?? cfg.lastScheduledAt ?? cfg.lastBackfillAt;
  if (!last) return true;
  return Date.now() - new Date(last).getTime() > 24 * 60 * 60 * 1000;
}

function dataFreshnessDate(cfg: IngestionConfig): string | null {
  const last = cfg.lastStoreActiveAt ?? cfg.lastScheduledAt;
  if (!last) return null;
  return new Date(last).toISOString().slice(0, 10);
}

// ─── Main Component ─────────────────────────────────────

export function IngestionStoreConfigs() {
  const { storeId: storeIdParam } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storeId = Number(storeIdParam);
  const [filter, setFilter] = useState<FilterOption>('All');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [acting, setActing] = useState<string | null>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // ─── Data fetching with auto-refresh ───────────────────

  const { data: configs, isLoading } = useQuery({
    queryKey: ['admin', 'ingestion', 'configs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<IngestionConfig[]>('/api/v1/ingestion/admin/configs', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load configs');
      return res.data;
    },
    refetchInterval: 15_000,
  });

  const { data: stores } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });

  const store = (stores ?? []).find((s) => s.id === storeId);
  const connectedSet = useMemo(() => new Set(store?.integrations ?? []), [store]);

  const storeConfigs = useMemo(
    () => (configs ?? []).filter((c) => c.storeId === storeId),
    [configs, storeId],
  );

  // ─── Filtered + sorted configs ────────────────────────

  const filteredConfigs = useMemo(() => {
    let list = [...storeConfigs];

    if (filter === 'Active') list = list.filter((c) => c.status === 'active');
    else if (filter === 'Backfilling') list = list.filter((c) => c.status === 'backfilling');
    else if (filter === 'Paused') list = list.filter((c) => c.status === 'paused');
    else if (filter === 'Stale') list = list.filter((c) => isStale(c));

    list.sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
    return list;
  }, [storeConfigs, filter]);

  // ─── Connected but not configured ─────────────────────

  const unconfiguredPlatforms = useMemo(() => {
    const configuredSet = new Set(storeConfigs.map((c) => c.platform));
    return [...connectedSet].filter((p) => !configuredSet.has(p));
  }, [storeConfigs, connectedSet]);

  // ─── Grouped by category ──────────────────────────────

  const grouped = useMemo(() => {
    const groups: Record<string, typeof filteredConfigs> = {};
    for (const cfg of filteredConfigs) {
      const cat = PLATFORM_META[cfg.platform]?.category ?? 'other';
      (groups[cat] ??= []).push(cfg);
    }
    return groups;
  }, [filteredConfigs]);

  // ─── Summary stats ────────────────────────────────────

  const summary = useMemo(() => {
    const active = storeConfigs.filter((c) => c.status === 'active').length;
    const backfilling = storeConfigs.filter((c) => c.status === 'backfilling').length;
    const paused = storeConfigs.filter((c) => c.status === 'paused').length;
    const disabled = storeConfigs.filter((c) => c.status === 'disabled').length;
    const stale = storeConfigs.filter(isStale).length;
    return { active, backfilling, paused, disabled, stale };
  }, [storeConfigs]);

  const refetchConfigs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'configs'] });
  }, [queryClient]);

  // ─── Quick actions ────────────────────────────────────

  const handlePullNow = useCallback(async (cfg: IngestionConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setActing(`pull-${cfg.id}`);
    try {
      await api.post('/api/v1/ingestion/admin/pull-now', { storeId: cfg.storeId, platform: cfg.platform });
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [refetchConfigs]);

  const handleTogglePause = useCallback(async (cfg: IngestionConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setActing(`pause-${cfg.id}`);
    try {
      const endpoint = cfg.status === 'paused' ? 'resume' : 'pause';
      await api.post(`/api/v1/ingestion/admin/${endpoint}/${cfg.id}`, {});
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [refetchConfigs]);

  const handleSeedConfigs = useCallback(async () => {
    setActing('seed');
    try {
      await api.post('/api/v1/ingestion/admin/seed-configs', { storeId });
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [storeId, refetchConfigs]);

  // ─── Bulk actions ─────────────────────────────────────

  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkPause = useCallback(async () => {
    setActing('bulk-pause');
    try {
      for (const id of selectedIds) {
        const cfg = storeConfigs.find((c) => c.id === id);
        if (cfg && cfg.status === 'active') {
          await api.post(`/api/v1/ingestion/admin/pause/${id}`, {});
        }
      }
      setSelectedIds(new Set());
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [selectedIds, storeConfigs, refetchConfigs]);

  const handleBulkResume = useCallback(async () => {
    setActing('bulk-resume');
    try {
      for (const id of selectedIds) {
        const cfg = storeConfigs.find((c) => c.id === id);
        if (cfg && cfg.status === 'paused') {
          await api.post(`/api/v1/ingestion/admin/resume/${id}`, {});
        }
      }
      setSelectedIds(new Set());
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [selectedIds, storeConfigs, refetchConfigs]);

  const handleBulkBackfill = useCallback(async () => {
    setActing('bulk-backfill');
    try {
      for (const id of selectedIds) {
        const cfg = storeConfigs.find((c) => c.id === id);
        if (cfg) {
          await api.post('/api/v1/ingestion/admin/pull-now', { storeId: cfg.storeId, platform: cfg.platform });
        }
      }
      setSelectedIds(new Set());
      refetchConfigs();
    } finally {
      setActing(null);
    }
  }, [selectedIds, storeConfigs, refetchConfigs]);

  // ─── Keyboard navigation ──────────────────────────────

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (filteredConfigs.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((prev) => Math.min(prev + 1, filteredConfigs.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusIdx >= 0 && focusIdx < filteredConfigs.length) {
        const cfg = filteredConfigs[focusIdx]!;
        navigate(`/ingestion/${storeId}/${cfg.id}`);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [filteredConfigs, focusIdx, navigate, storeId]);

  // Scroll focused card into view
  useEffect(() => {
    if (focusIdx < 0) return;
    const el = listRef.current?.querySelector(`[data-idx="${focusIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx]);

  // ─── Render ───────────────────────────────────────────

  return (
    <div>
      <Link to="/ingestion" className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3">
        <ChevronLeft className="w-4 h-4" /> All stores
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {store?.name ?? `Store #${storeId}`}
            <span className="text-gray-400 dark:text-gray-500 font-normal text-base ml-1.5">#{storeId}</span>
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {storeConfigs.length} configured &middot; {connectedSet.size} connected
            {unconfiguredPlatforms.length > 0 && (
              <span className="text-amber-600 ml-1">
                &middot; {unconfiguredPlatforms.length} missing config{unconfiguredPlatforms.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BackfillForm storeId={storeId} store={store ?? null} onTriggered={refetchConfigs} />
        </div>
      </div>

      {/* Summary cards */}
      {storeConfigs.length > 0 && (
        <div className="grid grid-cols-5 gap-2 mb-4">
          {([
            { label: 'Active', value: summary.active, color: 'text-green-600 dark:text-green-400', filterTo: 'Active' as const },
            { label: 'Backfilling', value: summary.backfilling, color: 'text-blue-600 dark:text-blue-400', filterTo: 'Backfilling' as const },
            { label: 'Paused', value: summary.paused, color: 'text-amber-600', filterTo: 'Paused' as const },
            { label: 'Disabled', value: summary.disabled, color: 'text-gray-400 dark:text-gray-500', filterTo: 'All' as const },
            { label: 'Stale (>24h)', value: summary.stale, color: summary.stale > 0 ? 'text-red-500' : 'text-gray-400 dark:text-gray-500', filterTo: 'Stale' as const },
          ]).map((card) => (
            <button
              key={card.label}
              onClick={() => setFilter(filter === card.filterTo ? 'All' : card.filterTo)}
              className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
                filter === card.filterTo && filter !== 'All' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
              }`}
            >
              <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</div>
              <div className={`text-lg font-semibold ${card.color}`}>{card.value}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filter chips + bulk actions */}
      {storeConfigs.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  filter === opt
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{selectedIds.size} selected</span>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">Clear</button>
              <button
                onClick={handleBulkPause}
                disabled={acting === 'bulk-pause'}
                className="px-2 py-0.5 text-xs border border-gray-200 dark:border-gray-800 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
              >
                Pause
              </button>
              <button
                onClick={handleBulkResume}
                disabled={acting === 'bulk-resume'}
                className="px-2 py-0.5 text-xs border border-gray-200 dark:border-gray-800 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
              >
                Resume
              </button>
              <button
                onClick={handleBulkBackfill}
                disabled={acting === 'bulk-backfill'}
                className="px-2 py-0.5 text-xs bg-gray-900 dark:bg-gray-950 text-white rounded hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Pull Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* Platform list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-56" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : storeConfigs.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <Database className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No ingestion configs for this store.</p>
          {connectedSet.size > 0 && (
            <button
              onClick={handleSeedConfigs}
              disabled={acting === 'seed'}
              className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {acting === 'seed' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create configs for {connectedSet.size} platform{connectedSet.size !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      ) : (
        <div ref={listRef} className="space-y-4">
          {/* Grouped configs */}
          {CATEGORY_ORDER.map((cat) => {
            const items = grouped[cat];
            if (!items || items.length === 0) return null;
            return (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{CATEGORY_LABELS[cat]}</span>
                  <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                </div>
                <div className="space-y-1.5">
                  {items.map((cfg) => {
                    const globalIdx = filteredConfigs.indexOf(cfg);
                    return (
                      <ConfigCard
                        key={cfg.id}
                        cfg={cfg}
                        storeId={storeId}
                        isConnected={connectedSet.has(cfg.platform)}
                        isSelected={selectedIds.has(cfg.id)}
                        isFocused={focusIdx === globalIdx}
                        isActing={acting}
                        dataIdx={globalIdx}
                        onNavigate={() => navigate(`/ingestion/${storeId}/${cfg.id}`)}
                        onPullNow={(e) => handlePullNow(cfg, e)}
                        onTogglePause={(e) => handleTogglePause(cfg, e)}
                        onToggleSelect={(e) => toggleSelect(cfg.id, e)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Other category (unknown platforms) */}
          {grouped['other'] && grouped['other'].length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Other</span>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
              </div>
              <div className="space-y-1.5">
                {grouped['other'].map((cfg) => {
                  const globalIdx = filteredConfigs.indexOf(cfg);
                  return (
                    <ConfigCard
                      key={cfg.id}
                      cfg={cfg}
                      storeId={storeId}
                      isConnected={connectedSet.has(cfg.platform)}
                      isSelected={selectedIds.has(cfg.id)}
                      isFocused={focusIdx === globalIdx}
                      isActing={acting}
                      dataIdx={globalIdx}
                      onNavigate={() => navigate(`/ingestion/${storeId}/${cfg.id}`)}
                      onPullNow={(e) => handlePullNow(cfg, e)}
                      onTogglePause={(e) => handleTogglePause(cfg, e)}
                      onToggleSelect={(e) => toggleSelect(cfg.id, e)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Connected but not configured */}
          {unconfiguredPlatforms.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider">Missing Configs</span>
                <div className="flex-1 h-px bg-amber-100 dark:bg-amber-950/40" />
              </div>
              <div className="space-y-1.5">
                {unconfiguredPlatforms.map((platform) => {
                  const pm = PLATFORM_META[platform];
                  return (
                    <div
                      key={platform}
                      className="bg-amber-50/50 rounded-xl border border-dashed border-amber-200 dark:border-amber-900/50 px-4 py-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center border bg-white dark:bg-gray-900 border-amber-200 dark:border-amber-900/50 shrink-0">
                            {pm?.icon ? (
                              <img src={pm.icon} alt={pm.label} className="w-5 h-5 opacity-60" />
                            ) : (
                              <Activity className="w-4 h-4 text-amber-400" />
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{pm?.label ?? platform}</span>
                            <p className="text-xs text-amber-600 mt-0.5">Connected but no ingestion config</p>
                          </div>
                        </div>
                        <button
                          onClick={handleSeedConfigs}
                          disabled={acting === 'seed'}
                          className="px-2.5 py-1 text-xs font-medium border border-amber-300 dark:border-amber-900/50 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-950/40 disabled:opacity-50 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Create
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No results for filter */}
          {filteredConfigs.length === 0 && storeConfigs.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">No configs match "{filter}" filter.</p>
              <button onClick={() => setFilter('All')} className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline">Clear filter</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Config Card ────────────────────────────────────────

function ConfigCard({
  cfg,
  storeId,
  isConnected,
  isSelected,
  isFocused,
  isActing,
  dataIdx,
  onNavigate,
  onPullNow,
  onTogglePause,
  onToggleSelect,
}: {
  cfg: IngestionConfig;
  storeId: number;
  isConnected: boolean;
  isSelected: boolean;
  isFocused: boolean;
  isActing: string | null;
  dataIdx: number;
  onNavigate: () => void;
  onPullNow: (e: React.MouseEvent) => void;
  onTogglePause: (e: React.MouseEvent) => void;
  onToggleSelect: (e: React.MouseEvent) => void;
}) {
  const s = STATUS_CONFIG[cfg.status] ?? STATUS_CONFIG['active']!;
  const StatusIcon = s.icon;
  const pm = PLATFORM_META[cfg.platform];
  const platformLabel = pm?.label ?? cfg.platform;
  const platformIcon = pm?.icon;
  const stale = isStale(cfg);
  const freshness = dataFreshnessDate(cfg);
  const lastActivity = cfg.lastStoreActiveAt ?? cfg.lastScheduledAt ?? cfg.lastBackfillAt;

  const isPulling = isActing === `pull-${cfg.id}`;
  const isPausing = isActing === `pause-${cfg.id}`;

  return (
    <div
      data-idx={dataIdx}
      className={`bg-white dark:bg-gray-900 rounded-xl border px-4 py-3 transition-all group ${
        isConnected
          ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm'
          : 'border-dashed border-gray-300 dark:border-gray-700 opacity-60'
      } ${isSelected ? 'ring-2 ring-blue-500 ring-offset-1 border-blue-300' : 'border-gray-200'}
        ${isFocused ? 'ring-2 ring-gray-900 ring-offset-1' : ''}
        ${stale ? 'border-l-2 border-l-amber-400' : ''}`}
      onClick={onNavigate}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Checkbox */}
          <button onClick={onToggleSelect} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            {isSelected ? (
              <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <Square className="w-4 h-4 text-gray-300" />
            )}
          </button>

          {/* Platform icon */}
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center border shrink-0 ${
            isConnected ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
          }`}>
            {platformIcon ? (
              <img src={platformIcon} alt={platformLabel} className="w-5 h-5" />
            ) : isConnected ? (
              <Activity className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            ) : (
              <Unplug className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            )}
          </div>

          <div className="min-w-0">
            {/* Platform name + status + stale warning */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{platformLabel}</span>
              <InfoBadge tip={
                cfg.status === 'active' ? 'Config is running normally. Daily pulls fire on schedule and data is being ingested.' :
                cfg.status === 'paused' ? 'Config is paused. No scheduled pulls will run. Resume to restart data ingestion.' :
                cfg.status === 'backfilling' ? 'Historical data is being backfilled. Daily pulls are paused until backfill completes.' :
                'Config is disabled, usually because the platform connection was lost.'
              }>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${s.className}`}>
                  <StatusIcon className="w-3 h-3" />
                  {s.label}
                </span>
              </InfoBadge>
              {!isConnected && (
                <InfoBadge tip="This platform's OAuth connection has been removed or expired. Reconnect from the Integrations page to resume data ingestion.">
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 font-medium">
                    <Unplug className="w-2.5 h-2.5" /> Disconnected
                  </span>
                </InfoBadge>
              )}
              {stale && (
                <InfoBadge tip="This store has at least one active ingestion config that hasn't pulled data in over 24 hours. This may indicate a connection issue or scheduling problem.">
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50 font-medium">
                    <AlertTriangle className="w-2.5 h-2.5" /> Stale
                  </span>
                </InfoBadge>
              )}
            </div>

            {/* Meta info row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" /> {cfg.timezone}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> <span title={cfg.scheduleCron}>{cronToHuman(cfg.scheduleCron)}</span>
              </span>
              {cfg.webhookEnabled && (
                <InfoBadge tip="Real-time webhooks are enabled for this platform. Data updates are pushed immediately in addition to scheduled pulls.">
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Zap className="w-3 h-3" /> Webhooks
                  </span>
                </InfoBadge>
              )}
              {freshness && (
                <InfoBadge tip="The most recent date for which data has been successfully ingested. If this is more than 1 day old, check the runs tab for errors.">
                  <span className="text-gray-400 dark:text-gray-500">Data up to: {freshness}</span>
                </InfoBadge>
              )}
            </div>

            {/* Last activity */}
            <div className="flex flex-wrap gap-x-4 mt-1 text-xs text-gray-400 dark:text-gray-500">
              {lastActivity && <span>Last activity: {timeAgo(lastActivity)}</span>}
              {cfg.lastBackfillAt && <span>Last backfill: {timeAgo(cfg.lastBackfillAt)}</span>}
            </div>
          </div>
        </div>

        {/* Right side: quick actions + chevron */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Quick actions — visible on hover */}
          {isConnected && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={onPullNow}
                disabled={isPulling || cfg.status === 'backfilling'}
                title="Pull Now"
                className="p-1.5 rounded-md border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 transition-colors"
              >
                {isPulling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={onTogglePause}
                disabled={isPausing || cfg.status === 'backfilling' || cfg.status === 'disabled'}
                title={cfg.status === 'paused' ? 'Resume' : 'Pause'}
                className="p-1.5 rounded-md border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 transition-colors"
              >
                {isPausing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : cfg.status === 'paused' ? (
                  <RefreshCw className="w-3.5 h-3.5" />
                ) : (
                  <Pause className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
        </div>
      </div>
    </div>
  );
}
